/**
 * 模块: 配置组件标签和描述常量
 * 职责: 提供配置维度和子组件的显示名称、描述、图标等
 * 限制: <150行
 */

import React from 'react';
import {
  BarChartOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';

// ============================================================================
// 指标维度定义
// ============================================================================

export interface MetricDimension {
  key: string;
  label: string;
  iconType: string; // 改为字符串类型，存储图标名称
  description: string;
  color: string;
}

// 图标映射表
export const ICON_MAP: Record<string, React.ComponentType> = {
  BarChartOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
  RocketOutlined,
  AppstoreOutlined,
};

export const METRIC_DIMENSIONS: MetricDimension[] = [
  {
    key: 'market_opportunity',
    label: '市场机会指数',
    iconType: 'BarChartOutlined',
    description: '评估市场可进入性和利润潜力',
    color: '#1890ff',
  },
  {
    key: 'entry_barrier',
    label: '进入壁垒指数',
    iconType: 'SafetyOutlined',
    description: '评估市场竞争难度和稳定性',
    color: '#52c41a',
  },
  {
    key: 'traffic_quality',
    label: '流量质量评分',
    iconType: 'ThunderboltOutlined',
    description: '评估关键词的流量效率和稳定性',
    color: '#faad14',
  },
  {
    key: 'ad_priority',
    label: '广告优先级评分',
    iconType: 'RocketOutlined',
    description: '综合评估广告投放优先级',
    color: '#722ed1',
  },
  {
    key: 'market_matrix',
    label: '市场矩阵分类',
    iconType: 'AppstoreOutlined',
    description: '基于机会和稳定性的四象限分类',
    color: '#eb2f96',
  },
];

// ============================================================================
// 子维度显示名称映射（使用 metricKey.componentName 作为唯一键）
// ============================================================================

export const COMPONENT_LABELS: Record<string, string> = {
  // 市场机会指数
  'market_opportunity.market_control': '市场控制度',
  'market_opportunity.replacement_rate': '可替换率',
  'market_opportunity.brand_concentration': '品牌集中度',
  'market_opportunity.conversion_advantage': '转化优势',
  'market_opportunity.brand_diversity': '品牌多样性',

  // 进入壁垒
  'entry_barrier.concentration': '垄断集中度',
  'entry_barrier.stability': '市场稳定性',
  'entry_barrier.diversity': '品牌多样性',
  'entry_barrier.fluidity': '市场流动性',

  // 流量质量
  'traffic_quality.efficiency': '流量效率',
  'traffic_quality.stability': '流量稳定性',

  // 广告优先级
  'ad_priority.rank_score': '排名得分',
  'ad_priority.opportunity_score': '机会得分',
  'ad_priority.quality_score': '质量得分',
  'ad_priority.trend_factor': '趋势因子',
};

export const COMPONENT_DESCRIPTIONS: Record<string, string> = {
  'market_opportunity.market_control': '头部品牌的市场控制力（反向，越低越好）',
  'market_opportunity.replacement_rate': '市场可被新进入者替代的程度',
  'market_opportunity.brand_concentration': '品牌集中度（反向，越分散越好）',
  'market_opportunity.conversion_advantage': '转化率相对优势',
  'market_opportunity.brand_diversity': '品牌数量的多样性',

  'entry_barrier.concentration': '市场被少数品牌垄断的程度',
  'entry_barrier.stability': '市场结构的稳定性',
  'entry_barrier.diversity': '品牌数量的丰富程度',
  'entry_barrier.fluidity': '市场的流动性和变化速度',

  'traffic_quality.efficiency': '流量转化效率',
  'traffic_quality.stability': '流量的稳定性',

  'ad_priority.rank_score': '基于进入壁垒的排名得分',
  'ad_priority.opportunity_score': '基于市场机会的得分',
  'ad_priority.quality_score': '基于流量质量的得分',
  'ad_priority.trend_factor': '市场趋势的影响因子',
};

// ============================================================================
// 辅助函数
// ============================================================================

/**
 * 获取组件的唯一键（用于查找标签和描述）
 */
export const getComponentKey = (metricKey: string, componentName: string): string => {
  return `${metricKey}.${componentName}`;
};

/**
 * 获取组件显示标签（带降级）
 */
export const getComponentLabel = (metricKey: string, componentName: string): string => {
  const key = getComponentKey(metricKey, componentName);
  return COMPONENT_LABELS[key] || componentName;
};

/**
 * 获取组件描述（带降级）
 */
export const getComponentDescription = (metricKey: string, componentName: string): string => {
  const key = getComponentKey(metricKey, componentName);
  return COMPONENT_DESCRIPTIONS[key] || '';
};

/**
 * 根据key获取指标维度信息
 */
export const getMetricDimension = (key: string): MetricDimension | undefined => {
  return METRIC_DIMENSIONS.find((dim) => dim.key === key);
};

/**
 * 根据iconType获取图标组件
 */
export const getIconComponent = (iconType: string): React.ReactNode => {
  const IconComponent = ICON_MAP[iconType];
  return IconComponent ? React.createElement(IconComponent) : null;
};

